import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Download, Loader2, Share2, Check, Calendar,
  Target, Sparkles, Heart, Mic, BarChart3, Users, Star,
  MapPin, Newspaper, TrendingUp, Megaphone, Lightbulb,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useReportData } from "../../hooks/useReportData";
import { useReportPdf } from "../../hooks/useReportPdf";
import { ReportLoading } from "./report/ReportLoading";
import { ReportError, ReportFailed } from "./report/ReportError";
import { ReportPositioning } from "./report/ReportPositioning";
import { ReportArchetype } from "./report/ReportArchetype";
import { ReportValues } from "./report/ReportValues";
import { ReportVoice } from "./report/ReportVoice";
import { ReportSwot } from "./report/ReportSwot";
import { ReportCompetitors } from "./report/ReportCompetitors";
import { ReportReviews } from "./report/ReportReviews";
import { ReportGBP } from "./report/ReportGBP";
import { ReportNews } from "./report/ReportNews";
import { ReportICP } from "./report/ReportICP";
import { ReportMarketing } from "./report/ReportMarketing";
import { ReportCampaigns } from "./report/ReportCampaigns";

const SECTIONS = [
  { id: "positioning", title: "Brand Positioning", icon: Target },
  { id: "archetype", title: "Brand Archetype", icon: Sparkles },
  { id: "values", title: "Values & Mission", icon: Heart },
  { id: "voice", title: "Brand Voice", icon: Mic },
  { id: "swot", title: "SWOT Analysis", icon: BarChart3 },
  { id: "competitors", title: "Competitors", icon: Users },
  { id: "reviews", title: "Google Reviews", icon: Star },
  { id: "gbp", title: "Google Business Profile", icon: MapPin },
  { id: "news", title: "News & PR", icon: Newspaper },
  { id: "icp", title: "Ideal Customer", icon: TrendingUp },
  { id: "marketing", title: "Marketing Strategy", icon: Megaphone },
  { id: "campaigns", title: "Campaign Ideas", icon: Lightbulb },
];

const ReportDisplay = ({ onBack }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("positioning");
  const [copied, setCopied] = useState(false);

  const { report, loading, error, retry } = useReportData(reportId);
  const { downloading: downloadingPdf, download: handleDownloadPdf } = useReportPdf(
    reportId,
    report?.brand_input?.company_name
  );

  const handleBack = () => (onBack ? onBack() : navigate("/create"));

  const handleShareReport = async () => {
    const shareUrl = `${window.location.origin}/report/${reportId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    toast.success("Report link copied to clipboard!", { duration: 3000 });
    setTimeout(() => setCopied(false), 2000);
  };

  // Loading / error / failed states
  if (loading || (!report && !error)) return <ReportLoading />;
  if (error) return <ReportError error={error} onRetry={retry} onBack={handleBack} />;
  if (report?.status === "processing" || report?.status === "pending") return <ReportLoading />;
  if (report?.status === "failed") return <ReportFailed onBack={handleBack} />;

  const strategy = report?.strategy || {};
  const reviewsData = report?.reviews_data || {};
  const brandInput = report?.brand_input || {};

  const renderSection = () => {
    switch (activeSection) {
      case "positioning": return <ReportPositioning strategy={strategy} />;
      case "archetype": return <ReportArchetype strategy={strategy} />;
      case "values": return <ReportValues strategy={strategy} />;
      case "voice": return <ReportVoice strategy={strategy} />;
      case "swot": return <ReportSwot strategy={strategy} />;
      case "competitors": return <ReportCompetitors strategy={strategy} />;
      case "reviews": return <ReportReviews reviewsData={reviewsData} brandInput={brandInput} />;
      case "gbp": return <ReportGBP reviewsData={reviewsData} brandInput={brandInput} />;
      case "news": return <ReportNews reviewsData={reviewsData} strategy={strategy} brandInput={brandInput} />;
      case "icp": return <ReportICP strategy={strategy} />;
      case "marketing": return <ReportMarketing strategy={strategy} />;
      case "campaigns": return <ReportCampaigns strategy={strategy} />;
      default: return <ReportPositioning strategy={strategy} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="report-display">
      {/* Sidebar */}
      <div className="w-72 bg-slate-900/50 border-r border-slate-800 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <Button onClick={handleBack} variant="ghost" className="text-slate-400 hover:text-white w-full justify-start" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="report-sidebar">
          {SECTIONS.map(({ id, title, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 text-sm transition-colors ${
                activeSection === id
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                  : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              }`}
              data-testid={`section-btn-${id}`}
            >
              <Icon className="w-4 h-4" /> {title}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 bg-slate-950/80 backdrop-blur-sm border-b border-slate-800 p-4 z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="report-company-name">
                {brandInput.company_name || "Brand Strategy Report"}
              </h1>
              <p className="text-slate-400 text-sm">
                {brandInput.industry} {brandInput.location && `· ${brandInput.location}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleShareReport}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="share-report-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2 text-emerald-400" /> : <Share2 className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Share"}
              </Button>
              <Button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {downloadingPdf ? "Generating..." : "Download PDF"}
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-8">
          <h2 className="text-2xl font-bold text-white mb-6" data-testid="section-heading">
            {SECTIONS.find((s) => s.id === activeSection)?.title || "Brand Positioning"}
          </h2>
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default ReportDisplay;
